import moment from "moment";
import { useCallback, useContext, useMemo } from "react";
import {
  BlankMediaTypeImporterSaleViewModel,
  ItemViewModel,
  SubmitBlankMediaImporterApplication,
} from "../../../../../../generated/data-contracts";
import {
  IBlankMediaUseTypeValue,
  blankMediaUseTypeValues,
} from "../../../../../../models";
import {
  IAvisEditableListFieldConfig,
  IRowState,
} from "../../../../../shared/components/editableList";
import {
  AvisCalendar,
  AvisDropdown,
  AvisInputInt,
} from "../../../../../shared/components/inputs";
import { AvisContext } from "../../../../../shared/context";
import { ApplicationSectionBase, ISectionSharedProps } from "../../forms";
import { fieldCollectionEquipmentAndCNCode } from "./fields/SharedFieldCollection";
import { indexArrayByKey } from "../../../../../../utils";

interface IBlankMediaApplicationSalesProps
  extends ISectionSharedProps<SubmitBlankMediaImporterApplication> {
  professionalUsers: ItemViewModel[] | undefined;
  manufacturers: ItemViewModel[] | undefined;
}
export function BlankMediaApplicationSales(
  props: Readonly<IBlankMediaApplicationSalesProps>,
) {
  const {
    bag,
    bagField,
    equipments,
    disabled,
    loading,
    professionalUsers,
    manufacturers,
  } = props;
  const { res } = useContext(AvisContext);

  const blankMediaUseTypeValuesMap = useMemo(
    () => indexArrayByKey(blankMediaUseTypeValues(res) || [], "value"),
    [res],
  );

  const sharedFields = useMemo(
    () =>
      fieldCollectionEquipmentAndCNCode<BlankMediaTypeImporterSaleViewModel>(
        equipments,
        res,
        disabled,
      ),
    [equipments, res, disabled],
  );

  const periodFrom = useMemo(() => {
    return bag.values?.dateFrom
      ? moment(bag.values?.dateFrom).toDate()
      : undefined;
  }, [bag.values?.dateFrom]);
  const periodTo = useMemo(() => {
    return bag.values?.dateTo ? moment(bag.values?.dateTo).toDate() : undefined;
  }, [bag.values?.dateTo]);

  const localAllPersons = useMemo(() => {
    return { profUsers: professionalUsers, manufUsers: manufacturers };
  }, [professionalUsers, manufacturers]);
  const availablePersonsProfessionalsMap = useMemo(
    () => indexArrayByKey(professionalUsers || [], "id"),
    [professionalUsers],
  );
  const availablePersonsManufacturersMap = useMemo(
    () => indexArrayByKey(manufacturers || [], "id"),
    [manufacturers],
  );
  const getAvailablePersons = useCallback(
    (valIn: BlankMediaTypeImporterSaleViewModel) => {
      if (!valIn.personBlankMediaUse) return [];

      return valIn.personBlankMediaUse === "ProfessionalUser"
        ? professionalUsers
        : manufacturers;
    },
    [manufacturers, professionalUsers],
  );
  const getAvailablePersonsMap = useCallback(
    (valIn: BlankMediaTypeImporterSaleViewModel) => {
      if (!valIn.personBlankMediaUse) return new Map<string, ItemViewModel>();

      return valIn.personBlankMediaUse === "ProfessionalUser"
        ? availablePersonsProfessionalsMap
        : availablePersonsManufacturersMap;
    },
    [availablePersonsManufacturersMap, availablePersonsProfessionalsMap],
  );
  const fieldCollection: IAvisEditableListFieldConfig<
    IRowState<BlankMediaTypeImporterSaleViewModel>
  >[] = useMemo(
    () => [
      ...sharedFields,
      {
        name: "personBlankMediaUse",
        title: res("blankMedia_Application_label_SoldToPersonType"),
        required: true,
        minWidth: 125,
        width: 150,
        transformViewValue: (fieldValue) => {
          return blankMediaUseTypeValuesMap.get(fieldValue)?.title ?? "";
        },
        renderField: (field, prefix, i, val, onChange) => {
          const useTypeValues = blankMediaUseTypeValues(res).filter(
            (item) => item.value != "Importer",
          );
          return (
            <AvisDropdown<IBlankMediaUseTypeValue>
              name={`${prefix}.${i}.${field.name}`}
              onChange={(e) => {
                onChange(e);
                bag.setFieldValue(`${prefix}.${i}.personId`, undefined);
              }}
              options={useTypeValues}
              optionValue="value"
              optionLabel="title"
              value={val[field.name]}
              hideClear
              required={field.required}
              disabled={disabled}
            />
          );
        },
      },
      {
        name: "personId",
        title: res("blankMedia_Application_label_SoldToPerson"),
        required: true,
        minWidth: 200,
        width: "50%",
        transformViewValue: (fieldValue, rowValue) => {
          return (
            getAvailablePersonsMap(rowValue).get(fieldValue)?.name ??
            `id:${fieldValue}`
          );
        },
        renderField: (field, prefix, i, val, onChange) => {
          const fieldName = `${prefix}.${i}.${field.name}`;

          return (
            <AvisDropdown<ItemViewModel>
              name={fieldName}
              onChange={onChange}
              options={getAvailablePersons(val)}
              optionValue="id"
              optionLabel="name"
              value={val.personId}
              hideClear
              required={field.required}
              filter
              emptyFilterMessage={res("message_NoRelevantItems")}
              disabled={disabled}
            />
          );
        },
      },
      {
        name: "count",
        title: res("blankMedia_Application_label_Count"),
        required: true,
        minWidth: 75,
        width: 75,
        renderField: (field, prefix, i, val, onChange) => {
          return (
            <AvisInputInt
              name={`${prefix}.${i}.${field.name}`}
              value={val[field.name]?.toString()}
              onChange={onChange}
              min={1}
              disabled={disabled}
              required={field.required}
              hideClear
            />
          );
        },
      },
      {
        name: "transactionDate",
        title: res("blankMedia_Application_label_TransactionDate"),
        required: true,
        minWidth: 100,
        width: 100,
        dataType: "Date",
        renderField: (field, prefix, i, val, onChange) => {
          const yearFrom = periodFrom?.getFullYear();
          const yearTo = periodTo?.getFullYear();
          const yearRangeStr = `${yearFrom}:${yearTo}`;

          return (
            <AvisCalendar
              name={`${prefix}.${i}.${field.name}`}
              value={
                val.transactionDate
                  ? moment(val.transactionDate).toDate()
                  : undefined
              }
              disabled={disabled}
              onChange={onChange}
              minDate={periodFrom}
              maxDate={periodTo}
              yearRange={yearRangeStr}
              defaultDateToShow={periodFrom}
              required={field.required}
              hideClear
            />
          );
        },
      },
    ],
    [
      bag,
      blankMediaUseTypeValuesMap,
      disabled,
      getAvailablePersons,
      getAvailablePersonsMap,
      periodFrom,
      periodTo,
      res,
      sharedFields,
    ],
  );

  return (
    <ApplicationSectionBase
      title={res("blankMedia_Application_section_I_Sales")}
      textToDisplay="portal_TDN_ImporterReport_Sell"
      bag={bag}
      bagField={bagField}
      initValue={
        { rowState: "Add" } as IRowState<BlankMediaTypeImporterSaleViewModel>
      }
      fieldCollection={fieldCollection}
      loading={loading}
      disabled={disabled}
      forceUpdateKey={{ loading, localAllPersons }}
    />
  );
}
